import { Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { InquiryForm } from "@/components/site/InquiryForm";
import { PhotoBackground } from "@/components/site/PhotoBackground";
import { FAQ } from "@/components/site/FAQ";
import { FIRM } from "@/data/content";

const HERO_IMG =
    "https://images.unsplash.com/photo-1727072258729-2e23be04fb67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHxkZXNlcnQlMjBtb3VudGFpbnN8ZW58MHx8fHwxNzkwOTg4NzQ3fDA&ixlib=rb-4.1.0&q=85";
const FAQ_IMG =
    "https://images.unsplash.com/photo-1618867374765-f7702badaf59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHxkZXNlcnQlMjByb2Nrc3xlbnwwfHx8MTc5MDk4ODc0N3ww&ixlib=rb-4.1.0&q=85";
const ROWS_IMG =
    "https://images.unsplash.com/photo-1702327661938-437880208346?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHxtaW5pbWFsaXN0JTIwYnVpbGRpbmd8ZW58MHx8fHwxNzkwOTg4NzQ3fDA&ixlib=rb-4.1.0&q=85";

const ROWS = [
    {
        icon: Mail,
        label: "Direct Email",
        value: FIRM.email,
        href: `mailto:${FIRM.email}`,
        note: "Principal-reviewed inbox",
        pending: false,
        testid: "contact-email-row",
    },
    {
        icon: Phone,
        label: "Direct Line",
        value: FIRM.phone,
        href: FIRM.phoneHref,
        note: "Tucson headquarters line",
        pending: false,
        testid: "contact-phone-row",
    },
    {
        icon: MapPin,
        label: "Headquarters",
        value: FIRM.hq,
        note: "United States · meetings by appointment",
        pending: false,
        testid: "contact-hq-row",
    },
    {
        icon: MapPin,
        label: "Mailing Address",
        value: "2100 N Wilmot Rd, Suite 316",
        note: "Tucson, AZ 85730",
        pending: false,
        testid: "contact-address-row",
    },
];

const PROTOCOL = [
    {
        title: "Principal review",
        body: "Every inquiry is read and answered by a principal of the firm. Expect a considered reply within two business days.",
    },
    {
        title: "Confidentiality from the first message",
        body: "Initial conversations are held in strict confidence regardless of whether an engagement follows. No inquiry is ever referenced externally.",
    },
    {
        title: "Secure channels",
        body: "For sensitive matters, encrypted communication channels are established once contact is made and identities are verified.",
    },
];

export default function Contact() {
    return (
        <>
            <PageMeta
                title="Contact | Nationwide Strategies LLC"
                description="Begin a confidential conversation with Nationwide Strategies LLC, a principal-led collection agency consulting firm headquartered in Tucson, Arizona."
            />
            <PageHero
                testid="contact-hero"
                kicker="Contact"
                lines={["Begin with a", "private conversation."]}
                lede="Engagements begin quietly; a call or a note is enough. Every inquiry is reviewed by a principal and held in strict confidence from the first message."
                image={HERO_IMG}
            />

            <section className="relative overflow-hidden border-b border-slate-800/70">
                <PhotoBackground src={FAQ_IMG} />
                <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
                    <Reveal className="lg:col-span-4">
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Before You Ask</p>
                        <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl">
                            Questions, answered plainly.
                        </h2>
                        <p className="mt-6 text-sm leading-relaxed text-slate-400">
                            What agency owners and creditor teams usually want to know before the
                            first conversation.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-8">
                        <FAQ />
                    </Reveal>
                </div>
            </section>

            <section className="relative overflow-hidden border-b border-slate-800/70">
                <PhotoBackground src={ROWS_IMG} />
                <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
                    <div className="border border-amber-500/70">
                        {ROWS.map((row, i) => (
                            <Reveal key={row.label} delay={i * 0.06}>
                                <div
                                    data-testid={row.testid}
                                    className="grid items-center gap-4 border-t border-amber-500/40 bg-[#0b0f16] p-8 transition-colors duration-300 first:border-t-0 hover:bg-[#0e141d] sm:grid-cols-12 sm:gap-8 sm:p-10"
                                >
                                    <div className="flex items-center gap-4 sm:col-span-3">
                                        <row.icon size={18} className="text-amber-500" aria-hidden="true" />
                                        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                                            {row.label}
                                        </span>
                                    </div>
                                    {row.href ? (
                                        <a
                                            href={row.href}
                                            data-testid={`${row.testid}-link`}
                                            className="font-serif text-2xl text-slate-100 transition-colors duration-300 hover:text-amber-400 sm:col-span-4 sm:text-3xl"
                                        >
                                            {row.value}
                                        </a>
                                    ) : (
                                        <span className="font-serif text-2xl text-slate-100 sm:col-span-4 sm:text-3xl">
                                            {row.value}
                                        </span>
                                    )}
                                    <div className="sm:col-span-5 sm:text-right">
                                        <p className="text-xs leading-relaxed text-slate-500">{row.note}</p>
                                        {row.pending && (
                                            <span className="mt-2 inline-block border border-amber-500/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                                                Pending Verification
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-2xl font-mono text-[11px] uppercase leading-relaxed tracking-[0.2em] text-slate-600">
                            {FIRM.coordinates} · Details are added to this page only after verification
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="border-b border-slate-800/70">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
                    <Reveal className="lg:col-span-4">
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Written Inquiry</p>
                        <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl">
                            Prefer to write?
                        </h2>
                        <p className="mt-6 text-sm leading-relaxed text-slate-400">
                            A brief note is enough. Your message goes directly to a principal's
                            inbox, and nothing you write here is shared beyond the firm.
                        </p>
                        <p className="mt-6 border-l-2 border-blue-500/60 pl-5 text-xs leading-relaxed text-slate-500">
                            Please omit case-sensitive details at this stage. Secure channels are
                            established once contact is made.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-8">
                        <InquiryForm />
                    </Reveal>
                </div>
            </section>

            <section className="border-b border-slate-800/70 bg-[#090d14]">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
                    <Reveal>
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Engagement Protocol</p>
                        <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                            What to expect when you reach out.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-6 lg:grid-cols-3">
                        {PROTOCOL.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.08} className="h-full">
                                <div data-testid={`protocol-card-${i + 1}`} className="h-full border border-amber-500/70 bg-[#0b0f16] p-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]">
                                    <ShieldCheck size={18} className="text-amber-500" aria-hidden="true" />
                                    <h3 className="mt-6 font-serif text-2xl font-medium text-slate-100">{p.title}</h3>
                                    <p className="mt-4 text-sm leading-relaxed text-slate-400">{p.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
